import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import {
	type ProethicaStatTile,
	type ProethicaStatValue,
	formatProethicaDurationSeconds,
} from "@features/report/helpers/proethica-report-stats";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, TextSkeleton, useDevice } from "@memorang/ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Card, Text } from "react-native-paper";

const statText = (value: ProethicaStatValue, t: TFunction): string => {
	switch (value.state) {
		case "count":
			return String(value.count);
		case "minutes":
			return value.minutes < 1
				? t("report.proethica.stats.underAMinute")
				: t("report.proethica.stats.minutes", { count: value.minutes });
		case "duration":
			return formatProethicaDurationSeconds(value.seconds);
		case "score":
			return `${value.numCorrect}/${value.numTotal}`;
		default:
			return t("report.proethica.stats.unavailable");
	}
};

const StatTile = ({ tile }: { tile: ProethicaStatTile }) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();
	return (
		<Card
			style={{
				backgroundColor: theme.colors.surfaceVariant,
				width: isMobile ? "48%" : undefined,
				flex: isMobile ? undefined : 1,
			}}
			mode="contained"
		>
			<Box padding={16} alignItems="center" style={{ gap: 6 }}>
				<Icon
					name={tile.icon}
					size={28}
					color={theme.colors.primary}
					accessible={false}
				/>
				{tile.value.state === "loading" ? (
					<Box height={20} width={56} justifyContent="center">
						<TextSkeleton numLines={1} height={14} />
					</Box>
				) : (
					<Text
						variant="labelLarge"
						style={{ color: theme.colors.onSurface, fontWeight: "600" }}
					>
						{statText(tile.value, t)}
					</Text>
				)}
				<Text
					variant="labelMedium"
					style={{ color: theme.colors.onSurfaceVariant, fontWeight: "600" }}
				>
					{t(`report.proethica.stats.${tile.key}`)}
				</Text>
			</Box>
		</Card>
	);
};

export const ReportStatTiles = ({ tiles }: { tiles: ProethicaStatTile[] }) => (
	<Box flexDirection="row" flexWrap="wrap" style={{ gap: 8 }}>
		{tiles.map((tile) => (
			<StatTile key={tile.key} tile={tile} />
		))}
	</Box>
);
